import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Link2, Check, X, Loader2 } from "lucide-react";

const clean = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export function SlugEditor({ business, onSaved }) {
  const [slug, setSlug] = useState(business?.slug || "");
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState(null); // {available, reason}
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => { setSlug(business?.slug || ""); }, [business?.slug]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const prefix = `${origin.replace(/^https?:\/\//, "")}/boutique/`;
  const changed = clean(slug) !== (business?.slug || "");

  useEffect(() => {
    const c = clean(slug);
    if (!changed) { setStatus(null); return; }
    if (c.length < 3) { setStatus({ available: false, reason: "too_short" }); return; }
    setChecking(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/business/slug-available?slug=${encodeURIComponent(c)}`);
        setStatus({ available: data.available, reason: data.reason });
      } catch { setStatus(null); }
      finally { setChecking(false); }
    }, 450);
    return () => clearTimeout(timer.current);
  }, [slug, changed]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/business/slug", { slug: clean(slug) });
      toast.success("Lien de la boutique mis à jour ✅");
      onSaved?.(data.slug);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Impossible de changer le lien.");
    } finally {
      setSaving(false);
    }
  };

  const reason = status?.reason;
  const msg = reason === "too_short" ? "3 caractères minimum" : reason === "reserved" ? "Lien réservé, choisissez-en un autre" : reason === "taken" ? "Déjà pris, essayez une variante" : null;

  return (
    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 mt-4" data-testid="slug-editor-card">
      <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Link2 className="h-5 w-5 text-[#E85D04]" /> Lien de ma boutique</h3>
      <p className="text-xs text-[#9c968a] mb-3">Choisissez un lien court et facile à retenir pour partager votre boutique. Utilisez des lettres, chiffres et tirets.</p>

      <div className="flex items-stretch rounded-xl overflow-hidden border border-[#E5E0D8] bg-[#F9F6F0] focus-within:border-[#E85D04]">
        <span className="px-3 flex items-center text-xs text-[#9c968a] bg-[#F0ECE4] border-r border-[#E5E0D8] break-all">{prefix}</span>
        <input
          data-testid="slug-input"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="ma-boutique"
          className="flex-1 h-12 px-3 bg-transparent outline-none text-sm"
        />
        <span className="px-3 flex items-center">
          {!changed ? null : checking ? <Loader2 className="h-4 w-4 animate-spin text-[#9c968a]" /> : status?.available ? <Check className="h-4 w-4 text-[#1B4332]" data-testid="slug-available" /> : <X className="h-4 w-4 text-[#D00000]" data-testid="slug-unavailable" />}
        </span>
      </div>
      {msg && <p className="text-xs text-[#D00000] mt-1.5" data-testid="slug-error">{msg}</p>}
      <p className="text-xs text-[#5C574F] mt-2">Aperçu : <span className="font-semibold text-[#1A1A1A] break-all" data-testid="slug-preview">{prefix}{clean(slug) || "…"}</span></p>

      <button
        data-testid="slug-save-btn"
        onClick={save}
        disabled={saving || !changed || (status && !status.available) || clean(slug).length < 3}
        className="w-full h-12 mt-4 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-50"
      >
        {saving ? "Enregistrement…" : "Enregistrer le nouveau lien"}
      </button>
    </div>
  );
}
